<template>
  <section
    :class="$style.comparison"
    aria-labelledby="audit-improvement-title"
  >
    <div :class="$style.header">
      <div :class="$style.heading">
        <h2 id="audit-improvement-title">How You've Improved</h2>
        <p>
          {{
            example
              ? 'See how this example intake addresses challenges from a fictional Audit.'
              : 'See how this intake helps address challenges from your Audit.'
          }}
        </p>
      </div>

      <div
        v-if="audits.length"
        :class="$style.auditControls"
      >
        <label
          :class="$style.selectLabel"
          for="hiring-kit-audit"
          >Audit</label
        >
        <select
          id="hiring-kit-audit"
          :class="$style.auditSelect"
          :value="selectedAuditId"
          :disabled="saving || example"
          @change="emitAuditSelection"
        >
          <option
            v-for="audit in audits"
            :key="audit.id"
            :value="audit.id"
          >
            {{ audit.auditName }} —
            {{ formatAuditUpdatedDate(audit.updatedAt) }}
          </option>
        </select>
        <button
          v-if="!example"
          type="button"
          :class="$style.textButton"
          @click="$emit('view-audit')"
        >
          View Audit →
        </button>
      </div>
    </div>

    <div
      v-if="!audits.length"
      :class="$style.emptyState"
    >
      <div>
        <h3>Complete an Audit to see how you're improving</h3>
        <p>
          Your completed Audit findings will be connected to this Hiring Kit.
        </p>
      </div>
      <button
        type="button"
        :class="$style.primaryButton"
        @click="$emit('start-audit')"
      >
        Complete an Audit
      </button>
    </div>

    <div
      v-else-if="improvements.length"
      :class="$style.table"
      role="table"
    >
      <div
        :class="$style.tableHeader"
        role="row"
      >
        <span role="columnheader">Audit finding</span>
        <span role="columnheader">Implemented Improvements</span>
        <span role="columnheader">Suggested Next Steps</span>
      </div>
      <div
        v-for="improvement in improvements"
        :key="improvement.id"
        :class="$style.row"
        role="row"
      >
        <p role="cell">{{ improvement.finding }}</p>
        <p role="cell">{{ improvement.implementedImprovement }}</p>
        <div role="cell">
          <button
            type="button"
            :class="$style.nextStepButton"
            @click="$emit('next-step', improvement.nextStep)"
          >
            {{ improvement.nextStepLabel }} →
          </button>
        </div>
      </div>
    </div>

    <div
      v-else
      :class="$style.noFindings"
    >
      This Audit did not identify recurring challenges that need to be mapped
      here.
    </div>
  </section>
</template>

<script setup lang="ts">
  import type { FullAudit } from '~/constants/auditTypes'
  import type {
    AuditHiringKitImprovement,
    AuditImprovementNextStep,
  } from '~/utils/auditHiringKitComparison'
  import { formatAuditUpdatedDate } from '~/utils/auditHiringKitComparison'

  defineProps<{
    audits: FullAudit[]
    selectedAuditId: string
    improvements: AuditHiringKitImprovement[]
    saving?: boolean
    example?: boolean
  }>()

  const emit = defineEmits<{
    'select-audit': [auditId: string]
    'view-audit': []
    'next-step': [step: AuditImprovementNextStep]
    'start-audit': []
  }>()

  function emitAuditSelection(event: Event) {
    emit('select-audit', (event.target as HTMLSelectElement).value)
  }
</script>

<style module>
  .comparison {
    overflow: hidden;
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-md);
    background: var(--color-surface-default);
  }

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-24);
    width: 100%;
    box-sizing: border-box;
    padding: var(--space-16) var(--space-16);
    background: var(--color-surface-muted);
  }

  .heading {
    flex: 1;
    min-width: 0;
  }

  .heading h2 {
    margin: 0;
    color: var(--color-text-primary);
    font: var(--type-title-small);
    letter-spacing: var(--type-title-small-tracking);
  }

  .heading p {
    margin: var(--space-2) 0 0;
    color: var(--color-text-secondary);
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
  }

  .auditControls {
    display: grid;
    grid-template-columns: auto minmax(340px, 1fr) auto;
    align-items: center;
    gap: var(--space-8);
    min-width: 520px;
  }

  .selectLabel {
    color: var(--color-text-secondary);
    font: var(--type-label-large-emphasized);
    letter-spacing: var(--type-label-large-emphasized-tracking);
  }

  .auditSelect {
    width: 100%;
    min-width: 340px;
    height: 38px;
    padding: 0 var(--space-40) 0 var(--space-12);
    border: 1px solid var(--color-border-default);
    border-radius: var(--radius-sm);
    background: var(--color-surface-default);
    color: var(--color-text-primary);
    font: inherit;
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
  }

  .auditSelect:focus-visible {
    outline: none;
    border-color: var(--color-interactive-primary);
    box-shadow: var(--elevation-md);
  }

  .auditSelect:disabled {
    color: var(--color-text-secondary);
    opacity: 1;
  }

  .textButton,
  .nextStepButton,
  .primaryButton {
    border: 0;
    background: transparent;
    color: var(--color-interactive-primary);
    cursor: pointer;
    font: inherit;
    white-space: nowrap;

    font: var(--type-label-large-emphasized);
    letter-spacing: var(--type-label-large-emphasized-tracking);
  }

  .textButton {
    padding: var(--space-8) 0;
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
  }

  .tableHeader,
  .row {
    display: grid;
    grid-template-columns: 0.95fr 1.5fr minmax(180px, 0.75fr);
    gap: var(--space-20);
    align-items: center;
  }

  .tableHeader {
    padding: var(--space-8) var(--space-16);
    border-top: 1px solid var(--color-border-default);
    border-bottom: 1px solid var(--color-border-default);
    background: var(--color-surface-default);
    color: var(--color-text-primary);
    font: var(--type-label-small-emphasized);
    letter-spacing: var(--type-label-small-emphasized-tracking);
    text-transform: uppercase;
  }

  .row {
    min-height: 54px;
    padding: var(--space-8) var(--space-16);
    border-bottom: 1px solid var(--color-surface-muted);
  }

  .row:last-child {
    border-bottom: 0;
  }

  .row p {
    margin: 0;
    color: var(--color-text-primary);
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
  }

  .row p:first-child {
    color: var(--color-text-primary);

    font: var(--type-body-medium-emphasized);
    letter-spacing: var(--type-body-medium-emphasized-tracking);
  }

  .nextStepButton {
    padding: var(--space-8) 0;
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
    text-align: left;
  }

  .textButton:hover,
  .nextStepButton:hover {
    text-decoration: underline;
  }

  .emptyState {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-20);
    padding: var(--space-16) var(--space-16);
    border-top: 1px solid var(--color-border-default);
  }

  .emptyState h3 {
    margin: 0;
    color: var(--color-text-primary);
    font: var(--type-label-large-emphasized);
    letter-spacing: var(--type-label-large-emphasized-tracking);
  }

  .emptyState p {
    margin: var(--space-2) 0 0;
    color: var(--color-text-secondary);
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
  }

  .primaryButton {
    padding: var(--space-8) var(--space-12);
    border-radius: var(--radius-sm);
    background: var(--color-interactive-primary);
    color: var(--color-text-on-brand);
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
  }

  .noFindings {
    padding: var(--space-12) var(--space-16);
    border-top: 1px solid var(--color-border-default);
    color: var(--color-text-secondary);
    font: var(--type-body-medium);
    letter-spacing: var(--type-body-medium-tracking);
  }

  @media (max-width: 980px) {
    .header {
      align-items: flex-start;
      flex-direction: column;
    }

    .auditControls {
      width: 100%;
      min-width: 0;
    }
  }
</style>
